<script lang="ts">
  import { tStore } from '../i18n'
  import type { CriterionStats } from '../api/analytics'

  interface Props {
    title: string
    variant: 'met' | 'unmet'
    items: CriterionStats[]
  }

  let { title, variant, items }: Props = $props()

  // Show a short list by default; the rest reveal on demand so a category with
  // dozens of one-off technologies doesn't dominate the page.
  const COLLAPSED_LIMIT = 5
  let expanded = $state(false)

  let visible = $derived(expanded ? items : items.slice(0, COLLAPSED_LIMIT))
  let hiddenCount = $derived(Math.max(0, items.length - COLLAPSED_LIMIT))
</script>

<section class="status-group">
  <h4 class="status-group__title">
    <span
      class="status-group__dot"
      class:status-group__dot--met={variant === 'met'}
      class:status-group__dot--unmet={variant === 'unmet'}
    ></span>
    {title}
    <span class="status-group__count">{items.length}</span>
  </h4>

  {#if items.length === 0}
    <p class="status-group__empty">{$tStore('views.certificateAnalysis.emptyGroup')}</p>
  {:else}
    {#if expanded && hiddenCount > 0}
      <button
        type="button"
        class="status-group__toggle"
        onclick={() => (expanded = false)}
        aria-expanded={true}
      >
        {$tStore('views.certificateAnalysis.viewLess')}
        <span class="status-group__chevron status-group__chevron--open" aria-hidden="true">▾</span>
      </button>
    {/if}

    <ul class="status-list">
      {#each visible as c (c.criterion)}
        {@const justification = c.justification?.trim()}
        <li
          class="status-item"
          class:status-item--met={variant === 'met'}
          class:status-item--unmet={variant === 'unmet'}
        >
          <span
            class="status-item__name"
            title={justification ? `${c.criterion} — ${justification}` : c.criterion}
          >
            {c.criterion}
          </span>
          <span
            class="status-item__freq"
            title={$tStore('views.certificateAnalysis.requiredTimes', { count: c.required })}
          >
            {c.required}×
          </span>
        </li>
      {/each}
    </ul>

    {#if hiddenCount > 0}
      <button
        type="button"
        class="status-group__toggle"
        onclick={() => (expanded = !expanded)}
        aria-expanded={expanded}
      >
        {expanded
          ? $tStore('views.certificateAnalysis.viewLess')
          : $tStore('views.certificateAnalysis.viewMore', { count: hiddenCount })}
        <span
          class="status-group__chevron"
          class:status-group__chevron--open={expanded}
          aria-hidden="true">▾</span
        >
      </button>
    {/if}
  {/if}
</section>

<style lang="scss">
  @use '../assets/styles/variables' as v;

  .status-group {
    min-width: 0;
  }

  .status-group__title {
    display: flex;
    align-items: center;
    gap: v.$space-sm;
    margin: 0 0 v.$space-md;
    font-size: 0.8125rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: v.$color-text-muted;
  }

  .status-group__dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;

    // --ok / --bad are declared on the page root (.analysis) and inherit here.
    &--met {
      background: var(--ok);
    }

    &--unmet {
      background: var(--bad);
    }
  }

  .status-group__count {
    margin-left: auto;
    padding: 0 v.$space-sm;
    border-radius: v.$radius-pill;
    background: v.$color-surface;
    color: v.$color-text;
    font-variant-numeric: tabular-nums;
  }

  .status-group__empty {
    margin: 0;
    color: v.$color-text-subtle;
    font-size: 0.875rem;
  }

  .status-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .status-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: v.$space-sm;
    padding: v.$space-xs v.$space-sm;
    border-radius: v.$radius-sm;
    font-size: 0.875rem;
    border-left: 3px solid transparent;

    &--met {
      background: rgba(22, 163, 74, 0.06);
      border-left-color: var(--ok);
    }

    &--unmet {
      background: rgba(220, 38, 38, 0.06);
      border-left-color: var(--bad);
    }
  }

  .status-item__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: v.$color-text;
  }

  .status-item__freq {
    flex-shrink: 0;
    color: v.$color-text-muted;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
  }

  .status-group__toggle {
    display: inline-flex;
    align-items: center;
    gap: v.$space-xs;
    margin-top: v.$space-sm;
    margin-bottom: v.$space-sm;
    padding: v.$space-xs 0;
    background: none;
    border: none;
    cursor: pointer;
    font: inherit;
    font-size: 0.8125rem;
    font-weight: 600;
    color: v.$color-primary;

    &:hover {
      color: v.$color-primary-hover;
    }

    &:focus-visible {
      outline: 2px solid v.$color-primary;
      outline-offset: 2px;
      border-radius: v.$radius-sm;
    }
  }

  .status-group__chevron {
    font-size: 0.7rem;
    line-height: 1;
    transition: transform 0.15s ease;

    &--open {
      transform: rotate(180deg);
    }
  }
</style>
